import React, { useEffect, useRef, useState } from 'react';
import { Col, Input, Row, Select, Tabs, Typography } from 'antd';
import LayerFeedTable from './LayerFeedTable';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { createForecast, editForecast, showForecast, storeForecast, updateForecast } from '@app/api/feedForecast.api';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import { useNavigate } from 'react-router-dom';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useTranslation } from 'react-i18next';

interface FeedForecastProps {
  id?: string;
  type: 'edit' | 'create' | 'view';
  pageTitle: string;
  editable: boolean;
  // eslint-disable-next-line @typescript-eslint/ban-types
  setCurrentBreadcrumName: Function;
}

interface DataType {
  key: string;
  id?: number;
  name: string;
  isParent?: boolean;
  parent_id?: number;
  [key: string]: string | number | boolean | undefined;
}

interface TableRef {
  getTableData: () => DataType[];
}

interface MonthOption {
  value: string;
  label: string;
}

const { Text } = Typography;
const { Option } = Select;

const FeedForecastComponent: React.FC<FeedForecastProps> = ({ type, editable, id }) => {
  const [feedData, setFeedData] = useState<any>(null);
  const [monthHeaders, setMonthHeaders] = useState<string[]>([]);
  const [monthOptions, setMonthOptions] = useState<MonthOption[]>([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [totalCapacity, setTotalCapacity] = useState<number>(0);
  const { addHeaderButton, removeHeaderButton } = useHeader();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Create refs to access the table data from each tab
  const tableRefs = useRef<{ [key: string]: TableRef }>({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        let data;

        // Fetch data based on component type
        if (type === 'create') {
          data = await createForecast();
        } else if (type === 'edit' && id) {
          data = await editForecast(Number(id));
          // Set the selected year from the fetched data if available
          if (data.pr_layer_feed_forecast && data.pr_layer_feed_forecast.year) {
            setSelectedYear(Number(data.pr_layer_feed_forecast.year));
          }
          if (data.pr_layer_feed_forecast && data.pr_layer_feed_forecast.total) {
            setTotalCapacity(Number(data.pr_layer_feed_forecast.total));
          }
        } else if (type === 'view' && id) {
          data = await showForecast(Number(id));
          // Set the selected year from the fetched data if available
          if (data.pr_layer_feed_forecast && data.pr_layer_feed_forecast.year) {
            setSelectedYear(Number(data.pr_layer_feed_forecast.year));
          }
          if (data.pr_layer_feed_forecast && data.pr_layer_feed_forecast.total) {
            setTotalCapacity(Number(data.pr_layer_feed_forecast.total));
          }
        }

        if (data) {
          setFeedData(data);
          setMonthHeaders(data.month_options.map((month: MonthOption) => month.label));
          setMonthOptions(data.month_options);

          // Process feed data if we're in edit or view mode
          if ((type === 'edit' || type === 'view') && data.pr_layer_feed_forecast) {
            processFeedForecastData(data);
          }
        }
      } catch (error) {
        console.error('Failed to fetch forecast data', error);
      }
    };

    fetchData();
  }, [type, id]);

  // Process feed forecast data for edit and view modes
  const processFeedForecastData = (data: any) => {
    if (!data.pr_layer_feed_forecast || !data.pr_layer_feed_forecast.pr_feed_forecast_types) {
      return;
    }

    // The processed data will be set in the next render cycle
    // when the table refs are available and can be updated
  };

  const years = Array.from({ length: 26 }, (_, i) => new Date().getFullYear() - i);

  const registerTableRef = (feedTypeId: number, ref: TableRef) => {
    if (ref) {
      tableRefs.current[feedTypeId] = ref;
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const submitData = {
        year: selectedYear.toString(),
        total: totalCapacity,
        pr_feed_forecast_types: feedData?.pr_feed_types?.map((feedType: any) => {
          const tableData = tableRefs.current[feedType.id]?.getTableData() || [];
          const parentTotal = tableData.reduce((sum, row) => sum + ((row.total as number) || 0), 0);
          const parentOwnUsage = tableData.reduce((sum, row) => sum + ((row.ownUsage as number) || 0), 0);

          return {
            pr_feed_forecast_type_id: feedType.id,
            pr_feed_type_id: feedType.id,
            total: parentTotal,
            own_usage: parentOwnUsage,
            pr_feed_forecast_feed_types: tableData.map((row) => ({
              pr_feed_type_id: row.id,
              total: row.total || 0,
              own_usage: row.ownUsage || 0,
              pr_feed_forecast_feed_type_details: monthOptions.map((mo) => ({
                month: mo.value,
                qty: Number(row[mo.label] || 0),
              })),
            })),
          };
        }),
      };

      if (type === 'create') {
        await storeForecast(submitData);
      } else if (type === 'edit' && id) {
        await updateForecast(Number(id), submitData);
      }

      navigate('/feed-forecasts');
    } catch (error) {
      console.error('Failed to save forecast data', error);
    } finally {
      setLoading(false);
    }
  };

  const mapForecastDataToTableFormat = (feedTypeId: number): DataType[] | null => {
    if (!feedData || !feedData.pr_layer_feed_forecast || !feedData.pr_layer_feed_forecast.pr_feed_forecast_types) {
      return null;
    }

    const forecastType = feedData.pr_layer_feed_forecast.pr_feed_forecast_types.find(
      (ft: any) => ft.pr_feed_type_id === feedTypeId,
    );

    if (!forecastType || !forecastType.pr_feed_forecast_feed_types) {
      return null;
    }

    return forecastType.pr_feed_forecast_feed_types.map((feedType: any) => {
      const row: DataType = {
        key: `existing-${feedType.id}`,
        id: feedType.pr_feed_type_id,
        total: Number(feedType.total) || 0,
        ownUsage: Number(feedType.own_usage) || 0,
        name: ' ',
      };

      // Add month values from details
      feedType.pr_feed_forecast_feed_type_details.forEach((detail: any) => {
        const monthLabel = monthOptions.find((mo) => mo.value === detail.month)?.label || '';
        if (monthLabel) {
          row[monthLabel] = Number(detail.qty) || 0;
        }
      });

      return row;
    });
  };

  useEffect(() => {
    if (editable) {
      addHeaderButton(
        'save-button',
        <BaseButton type="primary" onClick={handleSave} loading={loading} size="small">
          {t('Save')}
        </BaseButton>,
      );
      addHeaderButton(
        'cancel-button',
        <BaseButton onClick={() => navigate('/feed-productions')} size="small">
          {t('Cancel')}
        </BaseButton>,
      );
    } else if (id) {
      addHeaderButton(
        'edit-button',
        <BaseButton
          type="primary"
          onClick={() => navigate(`/edit-feed-forecast`, { state: { id } })}
          loading={loading}
          size="small"
        >
          {t('Edit')}
        </BaseButton>,
      );
    }

    return () => {
      removeHeaderButton('save-button');
      removeHeaderButton('cancel-button');
      removeHeaderButton('edit-button');
    };
  }, [loading, editable, id, addHeaderButton, removeHeaderButton, t, navigate]);

  return (
    <BaseCard>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <label>Total Feed milling capacity (MT/Month): </label>
          <Input
            style={{ width: 100, height: 32, marginLeft: 8 }}
            type="number"
            value={totalCapacity}
            onChange={(e) => setTotalCapacity(Number(e.target.value))}
            disabled={type === 'view'}
          />
          <label style={{ marginLeft: 20 }}>Year: </label>
          <Select
            size="small"
            style={{ width: 120, marginLeft: 8 }}
            value={selectedYear}
            onChange={setSelectedYear}
            disabled={type === 'view'}
          >
            {years.map((year) => (
              <Option key={year} value={year}>
                {year}
              </Option>
            ))}
          </Select>
        </Col>
        <Col>
          <Text strong>Annex VI</Text>
        </Col>
      </Row>
      <Tabs defaultActiveKey="1">
        {feedData?.pr_feed_types?.map((feedType: any, index: number) => (
          <Tabs.TabPane tab={feedType.type} key={index.toString()}>
            <LayerFeedTable
              data={feedType.children}
              formType={feedType.type}
              monthHeaders={monthHeaders}
              existingData={mapForecastDataToTableFormat(feedType.id)}
              readonly={type === 'view'}
              ref={(ref) => registerTableRef(feedType.id, ref as TableRef)}
            />
          </Tabs.TabPane>
        ))}
      </Tabs>
    </BaseCard>
  );
};

export default FeedForecastComponent;
